import React, { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const apiUrl = (path) => `${API_BASE_URL}${path}`;
const formatPrice = (value) => `Rs. ${Number(value || 0).toFixed(0)}`;
const createSessionId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `session-${Date.now()}-${Math.random().toString(16).slice(2)}`);

function ScratchCard({
  eyebrow,
  title,
  description,
  locked,
  lockedMessage,
  content,
  loading,
  error,
  emptyMessage,
  coverLabel,
  coverAccent,
  onScratchStart,
  resetKey,
}) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const scratchingRef = useRef(false);
  const startedRef = useRef(false);
  const measureRef = useRef(0);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    setRevealed(false);
    scratchingRef.current = false;
    startedRef.current = false;
  }, [resetKey]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;

    if (!canvas || !container || locked || revealed) {
      return undefined;
    }

    const context = canvas.getContext("2d");
    if (!context) {
      return undefined;
    }

    const drawCover = () => {
      const rect = container.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      const width = Math.max(rect.width, 1);
      const height = Math.max(rect.height, 1);

      canvas.width = width * ratio;
      canvas.height = height * ratio;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;

      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      context.globalCompositeOperation = "source-over";

      const gradient = context.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, "#f3e5ca");
      gradient.addColorStop(0.5, "#dbc093");
      gradient.addColorStop(1, coverAccent);

      context.fillStyle = gradient;
      context.fillRect(0, 0, width, height);

      context.fillStyle = "rgba(255, 255, 255, 0.2)";
      for (let i = -height; i < width; i += 24) {
        context.fillRect(i, 0, 8, height);
      }

      context.fillStyle = "rgba(82, 46, 18, 0.92)";
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.font = "700 22px Georgia, serif";
      context.fillText(coverLabel, width / 2, height / 2 - 8);

      context.font = "600 12px Segoe UI, sans-serif";
      context.fillText("Scratch to reveal", width / 2, height / 2 + 22);
    };

    drawCover();
    window.addEventListener("resize", drawCover);

    return () => {
      window.removeEventListener("resize", drawCover);
    };
  }, [coverAccent, coverLabel, locked, resetKey, revealed]);

  const measureReveal = () => {
    const now = Date.now();
    if (now - measureRef.current < 140) {
      return;
    }

    measureRef.current = now;

    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) {
      return;
    }

    const { data, width, height } = context.getImageData(0, 0, canvas.width, canvas.height);
    let transparentPixels = 0;
    const totalPixels = width * height;

    for (let index = 3; index < data.length; index += 4) {
      if (data[index] === 0) {
        transparentPixels += 1;
      }
    }

    if (transparentPixels / totalPixels > 0.2) {
      setRevealed(true);
    }
  };

  const scratch = (event) => {
    const canvas = canvasRef.current;
    if (!canvas || locked || revealed) {
      return;
    }

    const context = canvas.getContext("2d");
    if (!context) {
      return;
    }

    const rect = canvas.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    context.globalCompositeOperation = "destination-out";
    context.beginPath();
    context.arc(x, y, 15, 0, Math.PI * 2);
    context.fill();

    measureReveal();
  };

  const handlePointerDown = (event) => {
    if (locked || revealed) {
      return;
    }

    scratchingRef.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);

    if (!startedRef.current) {
      startedRef.current = true;
      onScratchStart();
    }

    scratch(event);
  };

  const handlePointerMove = (event) => {
    if (scratchingRef.current) {
      scratch(event);
    }
  };

  const handlePointerUp = (event) => {
    scratchingRef.current = false;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return (
    <section className={`scratch-card ${locked ? "scratch-card--locked" : ""}`}>
      <div className="scratch-card__copy">
        <p className="scratch-card__eyebrow">{eyebrow}</p>
        <h2>{title}</h2>
        <p>{description}</p>
      </div>

      <div
        ref={containerRef}
        className={`scratch-card__surface ${revealed && !locked ? "scratch-card__surface--revealed" : ""}`}
      >
        <div className={`scratch-card__result ${revealed && !locked ? "scratch-card__result--visible" : ""}`}>
          {locked ? (
            <div className="scratch-card__status">
              <span className="scratch-card__badge">Locked</span>
              <p>{lockedMessage}</p>
            </div>
          ) : loading ? (
            <div className="scratch-card__status">
              <span className="scratch-card__badge">Finding</span>
              <p>Checking the best match for your order...</p>
            </div>
          ) : error ? (
            <div className="scratch-card__status">
              <span className="scratch-card__badge scratch-card__badge--error">Oops</span>
              <p>{error}</p>
            </div>
          ) : content ? (
            <div className="scratch-card__content">{content}</div>
          ) : (
            <div className="scratch-card__status">
              <span className="scratch-card__badge">No offer</span>
              <p>{emptyMessage}</p>
            </div>
          )}
        </div>

        {!locked && !revealed && (
          <div className="scratch-card__tease">
            <span className="scratch-card__badge">Scratch Card</span>
            <p>{startedRef.current ? "Keep scratching to see the surprise." : "Use your finger to reveal it."}</p>
          </div>
        )}

        {!locked && !revealed && (
          <canvas
            ref={canvasRef}
            className="scratch-card__canvas"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerCancel={handlePointerUp}
          />
        )}
      </div>
    </section>
  );
}

function App() {
  const menuRailRef = useRef(null);
  const [menu, setMenu] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [activeSection, setActiveSection] = useState("All");
  const [search, setSearch] = useState("");
  const [screenError, setScreenError] = useState("");
  const [recommendedOffer, setRecommendedOffer] = useState(null);
  const [acceptedOffer, setAcceptedOffer] = useState(false);
  const [orderId, setOrderId] = useState("");
  const [tableSessionId, setTableSessionId] = useState("");
  const [recommendationEventId, setRecommendationEventId] = useState("");
  const [recommendationLoading, setRecommendationLoading] = useState(false);
  const [freeLoading, setFreeLoading] = useState(false);
  const [recommendationError, setRecommendationError] = useState("");
  const [freeError, setFreeError] = useState("");
  const [recommendationResetKey, setRecommendationResetKey] = useState(0);
  const [freeResetKey, setFreeResetKey] = useState(0);

  useEffect(() => {
    const storedSessionId = window.sessionStorage.getItem("restaurant_table_session_id");
    const nextSessionId = storedSessionId || createSessionId();
    window.sessionStorage.setItem("restaurant_table_session_id", nextSessionId);
    setTableSessionId(nextSessionId);
  }, []);

  useEffect(() => {
    let active = true;

    fetch(apiUrl("/menu"))
      .then((response) => {
        if (!response.ok) {
          throw new Error("Failed to load menu");
        }
        return response.json();
      })
      .then((data) => {
        if (active) {
          setMenu(data);
        }
      })
      .catch(() => {
        if (active) {
          setScreenError("Backend is not running or the menu could not be loaded.");
        }
      });

    return () => {
      active = false;
    };
  }, []);

  const sectionOptions = useMemo(() => {
    const sections = Array.from(new Set(menu.map((item) => item.section))).sort();
    return ["All", ...sections];
  }, [menu]);

  const filteredMenu = useMemo(() => {
    const query = search.trim().toLowerCase();
    return menu.filter((item) => {
      const matchesSection = activeSection === "All" || item.section === activeSection;
      const haystack = `${item.item} ${item.section}`.toLowerCase();
      return matchesSection && (!query || haystack.includes(query));
    });
  }, [activeSection, menu, search]);

  const groupedMenu = useMemo(() => {
    return filteredMenu.reduce((accumulator, item) => {
      if (!accumulator[item.section]) {
        accumulator[item.section] = [];
      }
      accumulator[item.section].push(item);
      return accumulator;
    }, {});
  }, [filteredMenu]);

  const priceMap = useMemo(() => {
    return menu.reduce((accumulator, item) => {
      accumulator[item.item] = Number(item.price) || 0;
      return accumulator;
    }, {});
  }, [menu]);

  const getPrice = (item) => priceMap[item] ?? 0;
  const visibleItems = groupedMenu[activeSection] || [];

  useEffect(() => {
    if (activeSection !== "All" && menuRailRef.current) {
      menuRailRef.current.scrollTo({ left: 0, behavior: "smooth" });
    }
  }, [activeSection]);

  const resetOfferFlow = () => {
    setRecommendedOffer(null);
    setAcceptedOffer(false);
    setRecommendationEventId("");
    setRecommendationError("");
    setFreeError("");
    setRecommendationLoading(false);
    setFreeLoading(false);
    setRecommendationResetKey((value) => value + 1);
    setFreeResetKey((value) => value + 1);
  };

  const handleSelect = (item) => {
    setScreenError("");
    resetOfferFlow();

    setSelectedItems((current) => {
      if (current.includes(item)) {
        return current.filter((entry) => entry !== item);
      }
      return [...current, item];
    });
  };

  const loadRecommendation = async () => {
    if (recommendationLoading || selectedItems.length === 0) {
      return;
    }

    setRecommendationLoading(true);
    setRecommendationError("");

    try {
      const response = await fetch(apiUrl("/recommend"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: selectedItems,
          order_id: orderId || null,
          table_session_id: tableSessionId || null,
        }),
      });

      if (!response.ok) {
        throw new Error("Could not fetch recommendation.");
      }

      const data = await response.json();
      setOrderId(data.order_id || "");
      setTableSessionId(data.table_session_id || tableSessionId);
      setRecommendationEventId(data.recommendation_event_id || "");
      setRecommendedOffer((data.recommendations || [])[0] || null);
      setAcceptedOffer(false);
      setFreeResetKey((value) => value + 1);
    } catch {
      setRecommendationError("Could not fetch recommendation.");
      setRecommendedOffer(null);
    } finally {
      setRecommendationLoading(false);
    }
  };

  const loadFreeItem = async () => {
    if (freeLoading || !recommendedOffer) {
      return;
    }

    setFreeLoading(true);
    setFreeError("");

    try {
      const response = await fetch(apiUrl("/free"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: selectedItems }),
      });

      if (!response.ok) {
        throw new Error("Could not fetch free item.");
      }

      const data = await response.json();
      if (!data.free) {
        throw new Error("No free item.");
      }
    } catch {
      setFreeError("Could not fetch free item.");
    } finally {
      setFreeLoading(false);
    }
  };

  const toggleAcceptedOffer = async () => {
    const nextAccepted = !acceptedOffer;
    setAcceptedOffer(nextAccepted);

    if (!recommendationEventId) {
      return;
    }

    try {
      await fetch(apiUrl("/offer/decision"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_id: recommendationEventId,
          accepted: nextAccepted,
        }),
      });
    } catch {
      // Keep customer flow smooth even if analytics logging fails.
    }
  };

  const selectedTotal = selectedItems.reduce((sum, item) => sum + getPrice(item), 0);
  const addonTotal = acceptedOffer && recommendedOffer ? recommendedOffer.addon_price : 0;
  const finalTotal = selectedTotal + addonTotal;

  const baseBillItems = selectedItems.map((item) => ({
    name: item,
    type: "Selected",
    price: getPrice(item),
  }));

  const billItems = [
    ...baseBillItems,
    ...(acceptedOffer && recommendedOffer
      ? [{ name: recommendedOffer.addon, type: "Recommended", price: recommendedOffer.addon_price }]
      : []),
    ...(acceptedOffer && recommendedOffer
      ? [{ name: recommendedOffer.free_item, type: "Free", price: 0 }]
      : []),
  ];

  return (
    <main className="app-shell">
      <section className="hero">
        <div className="hero__copy">
          <p className="hero__eyebrow">Navras Signature Table Experience</p>
          <h1>Discover a premium dining journey at Navras with curated pairings and a delightful complimentary surprise.</h1>
          <p className="hero__text">
            Choose your favorites from the Navras menu, then unveil a specially selected pairing and an exclusive complimentary indulgence crafted for your table.
          </p>
        </div>

        <div className="hero__panel">
          <div className="hero__image-shell">
            <img className="hero__image" src="/navras-interior.png" alt="Navras restaurant interior" />
            <div className="hero__image-badge">Navras Ambience</div>
          </div>

          <div className="hero__selection-card">
            <span className="hero__panel-label">Your Navras selections</span>
            <div className="hero__selection">
              {selectedItems.length > 0 ? selectedItems.join(", ") : "Select your first Navras delicacy to begin"}
            </div>
            <p className="hero__panel-note">Reveal both cards to discover what the Navras kitchen recommends for a more memorable meal.</p>
          </div>
        </div>
      </section>

      <section className="menu-panel">
        <div className="panel-top">
          <div>
            <p className="section-heading__eyebrow">Navras Menu</p>
            <h2>Browse the menu with a smooth sideways glide</h2>
          </div>

          <input
            className="search-input"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search dosa, paneer, soup, thali..."
          />
        </div>

        <div className="section-tabs">
          {sectionOptions.map((section) => (
            <button
              key={section}
              type="button"
              className={`section-tab ${activeSection === section ? "section-tab--active" : ""}`}
              onClick={() => setActiveSection(section)}
            >
              {section}
            </button>
          ))}
        </div>

        {screenError && <p className="app-error">{screenError}</p>}

        {activeSection === "All" ? (
          <div className="menu-overview">
            {Object.entries(groupedMenu).map(([section, items]) => (
              <button
                key={section}
                type="button"
                className="overview-card"
                onClick={() => setActiveSection(section)}
              >
                <span>{section}</span>
                <strong>{items.length} dishes</strong>
                <p>Open this collection to explore the Navras selections in style.</p>
              </button>
            ))}
          </div>
        ) : (
          <div className="menu-lane">
            <div className="menu-lane__top">
              <div>
                <h3>{activeSection}</h3>
                <span>{visibleItems.length} Navras dishes in this collection</span>
              </div>
              <div className="menu-lane__actions">
                <button
                  type="button"
                  className="lane-button"
                  onClick={() => menuRailRef.current?.scrollBy({ left: -320, behavior: "smooth" })}
                >
                  Scroll left
                </button>
                <button
                  type="button"
                  className="lane-button"
                  onClick={() => menuRailRef.current?.scrollBy({ left: 320, behavior: "smooth" })}
                >
                  Scroll right
                </button>
              </div>
            </div>

            <div className="menu-rail-hint">
              Swipe across on mobile or use the controls to explore the menu with ease.
            </div>

            <div ref={menuRailRef} className="menu-rail">
              {visibleItems.map((item) => {
                const checked = selectedItems.includes(item.item);

                return (
                  <label key={item.item} className={`menu-card menu-card--rail ${checked ? "menu-card--selected" : ""}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleSelect(item.item)}
                    />
                    <span className="menu-card__mini-tag">{item.cuisine.replaceAll("_", " ")}</span>
                    <strong>{item.item}</strong>
                    <span className="menu-card__price">{formatPrice(item.price)}</span>
                    <span className="menu-card__hint">{checked ? "Included in your dining selection" : "Tap to add this to your table"}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}
      </section>

      <section className="scratch-grid">
        <ScratchCard
          eyebrow="Scratch One"
          title="A Navras pairing selected for your table"
          description="Scratch to reveal a thoughtfully chosen recommendation designed to complement your current selection."
          locked={selectedItems.length === 0}
          lockedMessage="Choose at least one dish from the Navras menu first."
          loading={recommendationLoading}
          error={recommendationError}
          emptyMessage="A pairing is not ready for this selection yet."
          coverLabel="Reveal Pairing"
          coverAccent="#c88c3e"
          onScratchStart={loadRecommendation}
          resetKey={recommendationResetKey}
          content={
            recommendedOffer ? (
              <>
                <span className="scratch-card__badge">{recommendedOffer.strategy_tag}</span>
                <div className="scratch-card__offer">
                  <strong>{recommendedOffer.addon}</strong>
                  <span>{formatPrice(recommendedOffer.addon_price)}</span>
                </div>
                <p>{recommendedOffer.reason}</p>
              </>
            ) : null
          }
        />

        <ScratchCard
          eyebrow="Scratch Two"
          title="Your complimentary Navras delight"
          description="Scratch to preview the complimentary treat reserved for guests who add the chef's recommendation."
          locked={!recommendedOffer}
          lockedMessage="Reveal the chef's pairing first."
          loading={freeLoading}
          error={freeError}
          emptyMessage="Your complimentary treat is not ready yet."
          coverLabel="Reveal Delight"
          coverAccent="#d06a4d"
          onScratchStart={loadFreeItem}
          resetKey={freeResetKey}
          content={
            recommendedOffer ? (
              <>
                <span className="scratch-card__badge">Complimentary with pairing</span>
                <div className="scratch-card__offer">
                  <strong>{recommendedOffer.free_item}</strong>
                  <span className="scratch-card__free-line">Absolutely Free with your chef's pairing</span>
                </div>
                <p>Add the chef's recommended pairing and this special Navras delight is included at no extra cost.</p>
              </>
            ) : null
          }
        />
      </section>

      {recommendedOffer && (
        <section className="offer-panel">
          <div className="offer-panel__copy">
            <p className="section-heading__eyebrow">Navras Chef's Pairing</p>
            <h2>Add this pairing to unlock your complimentary delight</h2>
            <p>
              Add <strong>{recommendedOffer.addon}</strong> for {formatPrice(recommendedOffer.addon_price)} and enjoy <strong>{recommendedOffer.free_item}</strong> absolutely free as our compliment to your table.
            </p>
          </div>

          <button
            type="button"
            className={`offer-button ${acceptedOffer ? "offer-button--selected" : ""}`}
            onClick={toggleAcceptedOffer}
          >
            {acceptedOffer ? "Chef's pairing added to your table" : "Add chef's pairing"}
          </button>
        </section>
      )}

      <section className="bill-panel">
        <div className="bill-panel__top">
          <div>
            <p className="section-heading__eyebrow">Navras Bill Preview</p>
            <h2>Your table summary</h2>
          </div>
          <strong className="bill-panel__total">{formatPrice(finalTotal)}</strong>
        </div>

        <div className="bill-items">
          {billItems.length > 0 ? (
            billItems.map((entry) => (
              <div key={`${entry.type}-${entry.name}`} className="bill-items__row">
                <div>
                  <span className="bill-items__name">{entry.name}</span>
                  <span className="bill-items__type">{entry.type}</span>
                </div>
                <strong className={entry.type === "Free" ? "bill-items__free" : ""}>
                  {entry.type === "Free" ? "FREE" : formatPrice(entry.price)}
                </strong>
              </div>
            ))
          ) : (
            <p className="bill-empty">Select dishes to begin your Navras experience.</p>
          )}
        </div>

        <div className="bill-totals">
          <div className="bill-totals__row">
            <span>Selected dishes</span>
            <strong>{formatPrice(selectedTotal)}</strong>
          </div>
          <div className="bill-totals__row">
            <span>Chef's pairing</span>
            <strong>{formatPrice(addonTotal)}</strong>
          </div>
          {acceptedOffer && recommendedOffer && (
            <div className="bill-totals__row bill-totals__row--free">
              <span>Complimentary delight</span>
              <strong>FREE</strong>
            </div>
          )}
          <div className="bill-totals__row bill-totals__row--total">
            <span>Total to pay</span>
            <strong>{formatPrice(finalTotal)}</strong>
          </div>
        </div>
      </section>
    </main>
  );
}

export default App;
